/* eslint-disable react/no-unknown-property */
/** @jsxImportSource @compiled/react */
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import FormTextField from './FormTextField';
import FormError from './FormError';
import FieldGroup from './FieldGroup';
import PasswordField from './PasswordField';
import TallButton from './TallButton';
import { RecoveryTsvErrorCode, TsvRecoveryFieldError } from '../types';
import messages from '../pages/login/components/LoginForm.messages';
import passwordContainerMessages from './PasswordContainer.messages';
import { useTranslateFormErrorIfAvailable } from '../pages/signup/FormValidation.messages';
import { Box } from '@atlaskit/primitives/compiled';

interface Props {
  email?: string;
  error?: RecoveryTsvErrorCode | null;
  fieldErrors: TsvRecoveryFieldError;
  isLoading?: boolean;
  onFormClick?: () => void;
  onValidationError: (tsvFieldError: TsvRecoveryFieldError) => void;
  emailFieldLabel: React.ReactNode;
  title?: React.ReactNode;
  submitButtonText: React.ReactNode;
  onSubmitClick?: () => void;
  onSubmit?: (email: string, password: string) => void;
}

const styles = cssMap({
  form: {
    margin: 0,
  },
  buttonContainer: {
    marginTop: token('space.100'),
  },
});

type ErrorContentProps = {
  error: RecoveryTsvErrorCode;
};

const ErrorContent = ({ error }: ErrorContentProps) => {
  switch (error) {
    case 'tsvForm.password.valid':
      return (
        <FormattedMessage
          id="recovery.error.password.incorrect"
          defaultMessage="The password you entered is incorrect. Please try again."
        />
      );

    case 'tsvForm.password.error':
      return (
        <FormattedMessage
          id="recovery.error.unexpected"
          defaultMessage="Something went wrong while trying to contact the server. Please refresh the page and try again."
        />
      );

    default:
      return (
        <FormattedMessage
          id="recovery.error.unknown"
          defaultMessage="Something went wrong while attempting to login. Please try again."
        />
      );
  }
};

const AccountVerificationForm = (props: Props) => {
  const { formatMessage } = useIntl();
  const passwordFieldError = useTranslateFormErrorIfAvailable(props.fieldErrors.password);
  const email = props.email || '';

  const previousPasswordFieldError = useRef<string | undefined>(undefined);
  const [password, setPassword] = useState('');

  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    const { isLoading, onValidationError } = props;
    const validationErrors: TsvRecoveryFieldError = {};
    e.preventDefault();

    if (isLoading) {
      return;
    }

    if (password.trim() === '') {
      validationErrors.password = formatMessage(messages.passwordEmpty);
    }

    if (Object.keys(validationErrors).length) {
      onValidationError(validationErrors);
      return;
    }

    props.onSubmit && props.onSubmit(email, password);
  };

  const handlePasswordChange = (e: React.FormEvent<HTMLInputElement>) => {
    props.onValidationError({}); // clear validation errors
    setPassword(e.currentTarget.value);
  };

  useEffect(() => {
    const passwordError =
      passwordFieldError && passwordFieldError !== previousPasswordFieldError.current;

    if (passwordError) {
      passwordInputRef.current?.focus();
    }

    previousPasswordFieldError.current = passwordFieldError;
  }, [props.fieldErrors.password, passwordFieldError]);

  const { error, title, emailFieldLabel } = props;

  return (
    <div data-testid="email-verification-page-old-idf-testid">
      {error && (
        <FormError>
          <ErrorContent error={error} />
        </FormError>
      )}
      {title && <div>{title}</div>}
      <form
        css={styles.form}
        onSubmit={handleSubmit}
        id="form-accountverification-up"
        onClick={props.onFormClick}
        noValidate>
        <FieldGroup>
          <FormTextField
            id="email"
            name="email"
            type="email"
            autoComplete="off"
            placeholder={formatMessage(messages.emailEmpty)}
            label={emailFieldLabel}
            value={email}
            ref={emailInputRef}
            isDisabled={true}
            isRequired
          />
        </FieldGroup>
        <FieldGroup>
          <PasswordField
            name="password"
            autoComplete="new-password"
            id="password"
            testId="password" // used in pollinators
            placeholder={formatMessage(messages.passwordEmpty)}
            label={formatMessage(passwordContainerMessages.passwordLabel)}
            autoFocus={true}
            isInvalid={!!props.fieldErrors.password}
            invalidMessage={props.fieldErrors.password}
            onChange={handlePasswordChange}
            value={password}
            ref={passwordInputRef}
            isRequired
          />
        </FieldGroup>
        <Box xcss={styles.buttonContainer}>
          <TallButton
            appearance="primary"
            type="submit"
            isFullWidth
            id="tsvrecovery-submit" // used in pollinators
            onClick={() => props.onSubmitClick && props.onSubmitClick()}
            isLoading={props.isLoading}>
            {props.submitButtonText}
          </TallButton>
        </Box>
      </form>
    </div>
  );
};

export default AccountVerificationForm;
